import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";

import { API_URL } from "@/lib/siteConfig";
const API_BASE = API_URL;

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const emailParam = searchParams.get("email") || "";
  const navigate = useNavigate();
  const { setUserFromVerification } = useAuth();

  const [digits, setDigits] = useState(["", "", "", "", "", ""]);
  const [error, setError] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(60);
  const [resending, setResending] = useState(false);
  const [resendMsg, setResendMsg] = useState("");
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const verifyInFlightRef = useRef(false);

  // Countdown timer
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => setResendCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  // Focus first input
  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  // Redirect if no email
  useEffect(() => {
    if (!emailParam) navigate("/register", { replace: true });
  }, [emailParam, navigate]);

  const handleDigitChange = (index: number, value: string) => {
    // Handle paste of full code
    if (value.length > 1) {
      const pasted = value.replace(/\D/g, "").slice(0, 6);
      if (pasted.length >= 1) {
        const newDigits = [...digits];
        for (let i = 0; i < 6; i++) newDigits[i] = pasted[i] || "";
        setDigits(newDigits);
        setError("");
        inputRefs.current[Math.min(pasted.length, 5)]?.focus();
        if (pasted.length === 6) handleVerify(newDigits.join(""));
        return;
      }
    }

    const digit = value.replace(/\D/g, "").slice(-1);
    const newDigits = [...digits];
    newDigits[index] = digit;
    setDigits(newDigits);
    setError("");

    if (digit && index < 5) inputRefs.current[index + 1]?.focus();

    const code = newDigits.join("");
    if (code.length === 6) handleVerify(code);
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === "Enter") {
      const code = digits.join("");
      if (code.length === 6) handleVerify(code);
    }
  };

  const handleVerify = async (code?: string) => {
    const finalCode = code || digits.join("");
    if (finalCode.length !== 6) {
      setError("Please enter all 6 digits.");
      return;
    }
    if (verifyInFlightRef.current) return;
    verifyInFlightRef.current = true;
    setVerifying(true);
    setError("");

    try {
      const res = await fetch(`${API_BASE}/auth/verify-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: emailParam, code: finalCode }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Verification failed");

      // Store token and user, redirect to dashboard
      if (data.token) {
        localStorage.setItem("moremail_token", data.token);
        setUserFromVerification(data.user);
      }
      navigate("/dashboard", { replace: true });
    } catch (err: any) {
      setError(err.message || "Verification failed. Please try again.");
      setDigits(["", "", "", "", "", ""]);
      inputRefs.current[0]?.focus();
    } finally {
      setVerifying(false);
      verifyInFlightRef.current = false;
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0 || resending) return;
    setResending(true);
    setResendMsg("");
    setError("");

    try {
      const res = await fetch(`${API_BASE}/auth/resend-verification`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: emailParam }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to resend");

      setResendMsg("New code sent!");
      setResendCooldown(60);
      setDigits(["", "", "", "", "", ""]);
      inputRefs.current[0]?.focus();
    } catch (err: any) {
      setError(err.message || "Failed to resend. Please try again.");
    } finally {
      setResending(false);
    }
  };

  const maskedEmail = emailParam.replace(
    /^(.{2})(.*)(@.*)$/,
    (_, a, b, c) => a + "•".repeat(Math.min(b.length, 6)) + c
  );

  return (
    <div className="min-h-screen bg-paper flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl border border-black/[0.06] shadow-xl shadow-black/[0.04] p-8 sm:p-10 text-center">
          {/* Email icon */}
          <div className="w-14 h-14 rounded-full bg-coral/10 border border-coral/20 flex items-center justify-center mx-auto mb-5">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#FF5F45" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="4" width="20" height="16" rx="2" />
              <path d="M22 7l-10 6L2 7" />
            </svg>
          </div>

          <h2 className="font-display text-2xl font-bold text-ink mb-2">Check your email</h2>
          <p className="text-sm text-slate mb-6 leading-relaxed">
            We sent a 6-digit code to<br />
            <strong className="text-ink">{maskedEmail}</strong>
          </p>

          {/* OTP Inputs */}
          <div className="flex justify-center gap-2.5 mb-5">
            {digits.map((d, i) => (
              <input
                key={i}
                ref={(el) => { inputRefs.current[i] = el; }}
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={d}
                onChange={(e) => handleDigitChange(i, e.target.value)}
                onKeyDown={(e) => handleKeyDown(i, e)}
                onPaste={(e) => {
                  e.preventDefault();
                  const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
                  if (pasted) handleDigitChange(0, pasted);
                }}
                disabled={verifying}
                className={`w-12 h-14 text-center text-xl font-bold rounded-xl border-2 bg-paper/50 text-ink outline-none transition-colors font-body
                  ${d ? "border-coral/50" : "border-black/[0.08]"}
                  focus:border-coral focus:ring-2 focus:ring-coral/20
                  disabled:opacity-50`}
              />
            ))}
          </div>

          {/* Error */}
          {error && (
            <div className="px-4 py-2.5 mb-4 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">
              {error}
            </div>
          )}

          {/* Resend success */}
          {resendMsg && (
            <div className="px-4 py-2.5 mb-4 bg-green-50 border border-green-200 rounded-xl text-green-700 text-sm">
              {resendMsg}
            </div>
          )}

          {/* Verify button */}
          <button
            onClick={() => handleVerify()}
            disabled={verifying || digits.join("").length !== 6}
            className={`w-full py-3.5 rounded-xl text-sm font-bold transition-all
              ${digits.join("").length === 6
                ? "bg-coral text-white hover:bg-coral-dark shadow-md shadow-coral/25 cursor-pointer"
                : "bg-black/[0.04] text-slate cursor-not-allowed"
              }
              ${verifying ? "opacity-70 cursor-wait" : ""}`}
          >
            {verifying ? "Verifying..." : "Verify Email"}
          </button>

          {/* Resend */}
          <div className="mt-5 text-sm text-slate">
            Didn't receive the code?{" "}
            {resendCooldown > 0 ? (
              <span>Resend in {resendCooldown}s</span>
            ) : (
              <button
                onClick={handleResend}
                disabled={resending}
                className="text-coral font-semibold hover:underline disabled:opacity-50"
              >
                {resending ? "Sending..." : "Resend code"}
              </button>
            )}
          </div>

          {/* Back */}
          <button
            onClick={() => navigate("/register")}
            className="mt-3 text-sm text-slate hover:text-ink transition-colors"
          >
            ← Back to register
          </button>
        </div>
      </div>
    </div>
  );
}
